import { ActionIcon, Drawer, Flex, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useChatPanelStoreSessionSync } from '@/hooks/useChatPanelStoreSessionSync';
import { useLocalStore } from '@/hooks/useLocalStore';
import ChartInput from '@/pages/OrganizationPageAiChat/components/ChartInput';
import DialogItems from '@/pages/OrganizationPageAiChat/components/DialogItems';
import FeedbackModal from '@/pages/OrganizationPageAiChat/components/DialogItems/FeedbackModal';
import {
  OrganizationChatAiPageStore,
  OrganizationChatAiPageStoreProvider,
} from '@/stores/local/pages/OrganizationChatAiPageStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { useDevices } from '@/utils/devices';

import ChatPanelToolCallDrawers from './ChatPanelToolCallDrawers';

import s from './ChatPanel.module.scss';

type ChatPanelProps = {
  opened: boolean;
  onClose: () => void;
  organizationId: number;
  documentId?: string;
  seedText?: string;
  analysisResultId?: number;
};

const ChatPanel: React.FC<ChatPanelProps> = ({
  opened,
  onClose,
  organizationId,
  documentId,
  seedText,
  analysisResultId,
}) => {
  const sidebarStore = useSidebarDocumentsStore();
  const { isCompactViewport } = useDevices();
  const [searchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get('session_id');

  const chatStore = useLocalStore(
    () =>
      new OrganizationChatAiPageStore({
        organizationId,
        sidebarStore,
        isModelSelectionEnabled: () => false,
      }),
  );

  useChatPanelStoreSessionSync({
    opened,
    organizationId,
    documentId,
    sessionIdFromUrl,
    seedText,
    analysisResultId,
    store: chatStore,
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position={isCompactViewport ? 'bottom' : 'left'}
      size={isCompactViewport ? '90vh' : '55%'}
      overlayProps={isCompactViewport ? {} : { backgroundOpacity: 0 }}
      trapFocus={false}
      withCloseButton={false}
      classNames={{ content: s.root, body: s.root__body }}
    >
      <OrganizationChatAiPageStoreProvider store={chatStore}>
        <Flex direction="column" className={s.root__inner}>
          <Flex className={s.root__header} justify="space-between" align="center">
            <ActionIcon variant="subtle" size="sm" style={{ visibility: 'hidden' }} aria-hidden>
              <IconX size={16} />
            </ActionIcon>
            <Text fw={600} size="sm">
              Обсудить с ИИ
            </Text>
            <ActionIcon variant="subtle" size="sm" onClick={onClose} aria-label="Закрыть панель чата">
              <IconX size={16} />
            </ActionIcon>
          </Flex>
          <div className={s.root__dialog}>
            <DialogItems isDrawer drawerFixedDocumentId={documentId} />
          </div>
          <div className={s.root__input}>
            <ChartInput hideTips />
          </div>
          <FeedbackModal />
          <ChatPanelToolCallDrawers />
        </Flex>
      </OrganizationChatAiPageStoreProvider>
    </Drawer>
  );
};

export default observer(ChatPanel);
